import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Typography, IconButton, withStyles } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ShareIcon from '@material-ui/icons/Share';
import AddIcon from '@material-ui/icons/Add';
import styles from './styles';
import { useTranslation } from 'react-i18next';
import { toVideoURL } from '../../../actions/transforms';
import IMAGES from '../../../constants/images';
import {
    CIVIC_THEMES,
    FITNESS_THEME,
    NUTRITION_THEME,
    SUSTAINABILITY_THEME,
} from './constants';

const ActionPreview = ({
    classes,
    name,
    description,
    bonusPoints,
    coverPhoto,
    photoAuthorName,
    icon,
    videoLinks,
    links,
    scrollTarget,
    metadata,
    isMetadataLoading,
    selectedTheme,
}) => {
    const { t } = useTranslation();
    const [linkMetadata, setLinkMetadata] = useState([]);

    useEffect(() => {
        if (!isMetadataLoading && metadata != null) {
            const newLinkMetadata = links.map((link) => {
                const linkMeta = metadata[link];
                return {
                    title: linkMeta?.title || 'Default Title',
                    description: linkMeta?.description || link,
                    imageUrl: linkMeta?.imageUrl || '',
                    url: link,
                };
            });
            setLinkMetadata(newLinkMetadata);
        }
    }, [isMetadataLoading, metadata, links]);

    const newName = typeof name === 'object' ? name.en : name;
    const newDescription =
        typeof description === 'object' ? description.en : description;

    const topRef = useRef(null);
    const descriptionRef = useRef(null);
    const photoAuthorRef = useRef(null);
    const bonusPointsRef = useRef(null);
    const linkRefs = useRef([]);
    const videoRefs = useRef([]);

    useEffect(() => {
        // console.log('scrollTarget:', scrollTarget);
        if (
            (scrollTarget === 'name' ||
                scrollTarget === 'coverPhoto' ||
                scrollTarget === 'icon') &&
            topRef.current
        ) {
            topRef.current.scrollIntoView({ behavior: 'smooth' });
        } else if (scrollTarget === 'description' && descriptionRef.current) {
            descriptionRef.current.scrollIntoView({ behavior: 'smooth' });
        } else if (scrollTarget === 'photoAuthor' && photoAuthorRef.current) {
            photoAuthorRef.current.scrollIntoView({ behavior: 'smooth' });
        } else if (scrollTarget === 'bonusPoints' && bonusPointsRef.current) {
            bonusPointsRef.current.scrollIntoView({ behavior: 'smooth' });
        } else if (scrollTarget?.startsWith('link') && linkRefs.current) {
            const index = parseInt(scrollTarget.split('-')[1], 10); // Extract index from scrollTarget (e.g., 'link-0')
            if (linkRefs.current[index]) {
                linkRefs.current[index].scrollIntoView({ behavior: 'smooth' });
            }
        } else if (scrollTarget?.startsWith('video') && videoRefs.current) {
            const index = parseInt(scrollTarget.split('-')[1], 10); // Extract index from scrollTarget (e.g., 'video-0')
            if (videoRefs.current[index]) {
                videoRefs.current[index].scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [scrollTarget]);

    const bonusPointType =
        selectedTheme?.name === SUSTAINABILITY_THEME
            ? t('actions.create.ecoBonus')
            : selectedTheme?.name === FITNESS_THEME
            ? t('actions.create.fitnessBonus')
            : selectedTheme?.name === NUTRITION_THEME
            ? t('actions.create.nutritionBonus')
            : CIVIC_THEMES.includes(selectedTheme?.name)
            ? t('actions.create.civicBonus')
            : t('actions.create.ecoBonus');

    return (
        <div className={classes.container}>
            <div className={classes.headerContainer}>
                <h2>{t('notifications.appPreview')}</h2>
                <hr className={classes.divider}></hr>
            </div>
            <div className={classes.previewContainer}>
                <div
                    className={classes.imageContainer}
                    style={{ backgroundImage: `url(${coverPhoto})` }}
                    ref={topRef}
                >
                    <div className={classes.header}>
                        <span className={classes.time}>9:41</span>
                        <div className={classes.statusIcons}>
                            <svg
                                width="18"
                                height="11"
                                viewBox="0 0 18 11"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    x="0"
                                    y="7"
                                    width="3"
                                    height="4"
                                    rx="0.5"
                                    fill="white"
                                />
                                <rect
                                    x="5"
                                    y="5"
                                    width="3"
                                    height="6"
                                    rx="0.5"
                                    fill="white"
                                />
                                <rect
                                    x="10"
                                    y="3"
                                    width="3"
                                    height="8"
                                    rx="0.5"
                                    fill="white"
                                />
                                <rect
                                    x="15"
                                    y="0"
                                    width="3"
                                    height="11"
                                    rx="0.5"
                                    fill="white"
                                />
                            </svg>
                            <svg
                                width="15"
                                height="11"
                                viewBox="0 0 15 11"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M7.5 2.1C9.4 2.1 11.1 2.9 12.3 4.1L13.8 2.6C12.2 1 9.9 0 7.5 0C5.1 0 2.8 1 1.2 2.6L2.7 4.1C3.9 2.9 5.6 2.1 7.5 2.1ZM7.5 5.9C8.4 5.9 9.3 6.3 9.9 6.9L11.4 5.4C10.4 4.4 9 3.8 7.5 3.8C6 3.8 4.6 4.4 3.6 5.4L5.1 6.9C5.7 6.3 6.6 5.9 7.5 5.9Z"
                                    fill="white"
                                />
                                <path
                                    d="M7.5 7.2C8.2 7.2 8.8 7.6 9.1 8.1L7.5 10L5.9 8.1C6.2 7.6 6.8 7.2 7.5 7.2Z"
                                    fill="white"
                                />
                            </svg>
                            <svg
                                width="25"
                                height="12"
                                viewBox="0 0 25 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    x="0.5"
                                    y="0.5"
                                    width="21"
                                    height="11"
                                    rx="2.5"
                                    stroke="white"
                                />
                                <rect
                                    x="2"
                                    y="2"
                                    width="18"
                                    height="8"
                                    rx="1"
                                    fill="white"
                                />
                                <path
                                    d="M23 4V8C23.8047 7.66122 24.328 6.87313 24.328 6C24.328 5.12687 23.8047 4.33878 23 4Z"
                                    fill="white"
                                />
                            </svg>
                        </div>
                    </div>
                    <div className={classes.navigationButtons}>
                        <IconButton className={classes.backButton}>
                            <ArrowBackIcon />
                        </IconButton>
                        <IconButton className={classes.shareButton}>
                            <ShareIcon />
                        </IconButton>
                    </div>
                    <div className={classes.actionInfoContainer}>
                        <div className={classes.actionIcon}>
                            {icon ? (
                                <img
                                    src={icon}
                                    alt="Action Icon"
                                    width="50"
                                    height="50"
                                />
                            ) : (
                                <div className={classes.placeholderIcon} />
                            )}
                        </div>
                        <div className={classes.actionTextContainer}>
                            <Typography
                                variant="h4"
                                className={classes.actionTitle}
                            >
                                {newName || t('actions.create.name')}
                            </Typography>
                            <div className={classes.logActionButton}>
                                <AddIcon /> {t('logAction')}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.content}>
                    <Typography
                        className={classes.description}
                        ref={descriptionRef}
                    >
                        {newDescription || t('actions.create.description')}
                    </Typography>
                    <div
                        className={classes.sectionContainer}
                        ref={bonusPointsRef}
                    >
                        <Typography className={classes.sectionTitle}>
                            {t('actions.create.bonusPoints')}
                        </Typography>
                        <div className={classes.pointsContainer}>
                            <div className={classes.pointItem}>
                                <div className={classes.pointCircle}>
                                    {bonusPoints}
                                </div>
                                <Typography className={classes.pointLabel}>
                                    {bonusPointType}
                                </Typography>
                            </div>
                            {/* <div className={classes.pointItem}>
                                <div className={classes.pointCircle}>{bonusPoints}</div>
                                <Typography className={classes.pointLabel}>{t('actions.create.walletBonus')}</Typography>
                            </div> */}
                        </div>
                    </div>
                    {/* Goals Section (Unavailable right now) */}
                    {/* <div className={classes.content}>
                    <div className={classes.goalsContainer}>
                        {goals.length > 0 ? (
                            goals.map((goal, index) => (
                                <div key={index} className={classes.goal}>
                                    <Typography variant="body1">{goal.title}</Typography>
                                    <Typography variant="body2">{goal.description}</Typography>
                                </div>
                            ))
                        ) : (
                            <div className={classes.noGoals}>
                                <div className={classes.noGoalsImage}></div>
                                <div className={classes.noGoalsText}>
                                    <Typography variant="body1">No goals set</Typography>
                                    <Typography variant="body2">Add goals to your action to see them here</Typography>
                                </div>
                            </div>
                        )}
                    </div>
                </div> */}
                    <div className={classes.sectionContainer}>
                        <Typography className={classes.sectionTitle}>
                            {t('actions.create.videos')}
                        </Typography>
                        <div className={classes.videoContainer}>
                            {Array.isArray(videoLinks) &&
                            videoLinks.length > 0 ? (
                                videoLinks.map((videoLink, index) => (
                                    <div
                                        key={index}
                                        className={classes.videoWrapper}
                                        ref={(el) =>
                                            (videoRefs.current[index] = el)
                                        }
                                    >
                                        <iframe
                                            title={`video preview ${index}`}
                                            src={toVideoURL(videoLink)} // Ensure `toVideoURL` is defined and returns a valid URL
                                            allow="accelerometer; autoplay; encrypted-media; gyroscope"
                                            allowFullScreen
                                            className={classes.previewVideo}
                                        />
                                    </div>
                                ))
                            ) : (
                                <Typography className={classes.noLinksText}>
                                    {t('actions.create.noVideos')}
                                </Typography>
                            )}
                        </div>
                    </div>
                    <div className={classes.sectionContainer}>
                        <Typography className={classes.sectionTitle}>
                            {t('actions.create.links')}
                        </Typography>
                        <div className={classes.linkContainer}>
                            {linkMetadata.length > 0 ? (
                                linkMetadata.map((meta, index) =>
                                    meta.url ? (
                                        <div
                                            key={index}
                                            className={classes.linkPreview}
                                        >
                                            <div
                                                className={
                                                    classes.previewImageWrapper
                                                }
                                                ref={(el) =>
                                                    (linkRefs.current[
                                                        index
                                                    ] = el)
                                                }
                                            >
                                                <img
                                                    src={
                                                        meta.imageUrl ||
                                                        IMAGES.defaultImage
                                                    }
                                                    className={
                                                        classes.previewImage
                                                    }
                                                    alt={meta.title}
                                                />
                                            </div>
                                            <div
                                                className={
                                                    classes.previewSummary
                                                }
                                            >
                                                <Typography
                                                    variant="h5"
                                                    className={
                                                        classes.previewSummaryTitle
                                                    }
                                                >
                                                    {meta.title}
                                                </Typography>
                                                <Typography
                                                    className={
                                                        classes.previewSummaryDescription
                                                    }
                                                >
                                                    {meta.description ||
                                                        meta.url}
                                                </Typography>
                                            </div>
                                        </div>
                                    ) : (
                                        <Typography
                                            className={classes.noLinksText}
                                        >
                                            {t('actions.create.noLinks')}
                                        </Typography>
                                    ),
                                )
                            ) : (
                                <Typography className={classes.noLinksText}>
                                    {t('actions.create.noLinks')}
                                </Typography>
                            )}
                        </div>
                    </div>
                    <div className={classes.photoCredit} ref={photoAuthorRef}>
                        <Typography
                            variant="body2"
                            className={classes.photoCreditText}
                        >
                            {photoAuthorName ? (
                                <>
                                    {t('actions.create.photo')}{' '}
                                    <span>&copy;</span> {photoAuthorName}
                                </>
                            ) : null}
                        </Typography>
                    </div>
                </div>
            </div>
            <div className={classes.footerContainer}>
                <p>{t('notifications.previewFooter')}</p>
            </div>
        </div>
    );
};

ActionPreview.propTypes = {
    classes: PropTypes.object.isRequired,
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    bonusPoints: PropTypes.number,
    coverPhoto: PropTypes.string,
    photoAuthorName: PropTypes.string,
    icon: PropTypes.string,
    videoLinks: PropTypes.arrayOf(PropTypes.string),
    links: PropTypes.arrayOf(PropTypes.string),
    scrollTarget: PropTypes.string,
    metadata: PropTypes.object.isRequired,
    isMetadataLoading: PropTypes.bool.isRequired,
    selectedTheme: PropTypes.object.isRequired,
};

ActionPreview.defaultProps = {
    name: 'Action Name',
    description: 'Action description will appear here.',
    bonusPoints: 0,
    coverPhoto: '',
    photoAuthorName: 'Cover Image Author',
    icon: null,
    videoLinks: [],
    links: [],
    scrollTarget: null,
};

export default withStyles(styles)(ActionPreview);
